import { useOverlayDismiss } from '@hooks/useOverlayDismiss'
import React, { useEffect, useRef, useState } from 'react'
import { ViewerBackdrop, ViewerButton, ViewerCloseButton, ViewerContainer, ViewerControls, ViewerCount } from './FileViewer.styles'

/** How far a swipe must travel, in px, to turn the page. */
const SWIPE_DISTANCE = 50

interface PhotoGalleryProps {
	/** Shown in order; the gallery opens on `startIndex`. */
	photos: { id: string; url: string }[]
	/** The photo to open on, or `null` while the gallery is closed. */
	startIndex: number | null
	onClose: () => void
}

/**
 * Several photos full screen, one at a time, on the app's lightbox chrome: previous and
 * next buttons, the arrow keys or a swipe move between them, and close via button /
 * backdrop / Escape. The Chat's photos open here, as iOS's swipeable gallery does.
 */
const PhotoGallery: React.FC<PhotoGalleryProps> = ({ photos, startIndex, onClose }) => {
	const isOpen = startIndex !== null && photos.length > 0
	const [index, setIndex] = useState(startIndex ?? 0)
	const touchStart = useRef<number | null>(null)
	useOverlayDismiss(isOpen, onClose)

	useEffect(() => {
		if (startIndex !== null) setIndex(startIndex)
	}, [startIndex])

	const step = (by: number) => setIndex(current => Math.min(Math.max(current + by, 0), photos.length - 1))

	useEffect(() => {
		if (!isOpen) return
		const handleKey = (e: KeyboardEvent) => {
			if (e.key === 'ArrowLeft') setIndex(current => Math.max(current - 1, 0))
			if (e.key === 'ArrowRight') setIndex(current => Math.min(current + 1, photos.length - 1))
		}
		document.addEventListener('keydown', handleKey)
		return () => document.removeEventListener('keydown', handleKey)
	}, [isOpen, photos.length])

	if (!isOpen) return null
	const photo = photos[Math.min(index, photos.length - 1)]

	return (
		<ViewerBackdrop onClick={onClose}>
			<ViewerContainer
				onClick={e => e.stopPropagation()}
				onTouchStart={e => {
					touchStart.current = e.touches[0]?.clientX ?? null
				}}
				onTouchEnd={e => {
					const start = touchStart.current
					const end = e.changedTouches[0]?.clientX
					touchStart.current = null
					if (start == null || end == null || Math.abs(end - start) < SWIPE_DISTANCE) return
					step(end < start ? 1 : -1)
				}}
			>
				<ViewerCloseButton onClick={onClose} aria-label="Close">
					✕
				</ViewerCloseButton>
				<img src={photo.url} alt={`Photo ${index + 1} of ${photos.length}`} />
				{photos.length > 1 && (
					<ViewerControls>
						<ViewerButton onClick={() => step(-1)} disabled={index === 0} aria-label="Previous photo">
							‹ Previous
						</ViewerButton>
						<ViewerCount>
							{index + 1} / {photos.length}
						</ViewerCount>
						<ViewerButton onClick={() => step(1)} disabled={index === photos.length - 1} aria-label="Next photo">
							Next ›
						</ViewerButton>
					</ViewerControls>
				)}
			</ViewerContainer>
		</ViewerBackdrop>
	)
}

export default PhotoGallery
